<script setup lang="ts">
import { computed, onMounted, ref, watch, useTemplateRef, onUnmounted } from 'vue';
import { useEventListener } from '@vueuse/core';
import { hexToHsl } from '@getaccept/lib-shared/src/helpers';
import type { HexColor } from '@getaccept/fried-tofu';

interface Props {
  color?: HexColor;
}

const props = withDefaults(defineProps<Props>(), {
  color: '#23C29C',
});

const targetColor = computed(() => hexToHsl(props.color));

const canvas = useTemplateRef<HTMLCanvasElement>('canvas');
const ctx = ref<CanvasRenderingContext2D | null>(null);

const CONFIG = {
  motion: 2,
  colorSpan: 30,
  hueDelta: 0.5,
  targetFps: 30,
};

const randomBetween = (min: number, max: number) => Math.random() * (max - min) + min;

type Circle = {
  x: number;
  y: number;
  dx: number;
  dy: number;
  radius: number;
  color: { h: number; s: number; l: number };
  dHue: number;
};

const circles = ref<Circle[]>([]);
let lastFrameTime = 0;
let animationFrameId: number | null = null;
const frameInterval = 1000 / CONFIG.targetFps;

const createCircles = () => {
  circles.value = [];
  const circleCount = Math.max(3, Math.min(12, window.innerWidth / 150));
  for (let i = 0; i < circleCount; i++) {
    const radius = Math.round(randomBetween(window.innerWidth / 8, window.innerWidth / 5));

    const x = randomBetween(radius, (canvas.value?.width || window.innerWidth) - radius);
    const y = randomBetween(radius, (canvas.value?.height || window.innerHeight) - radius);
    const dx = randomBetween(window.innerWidth / -1000, window.innerWidth / 2000) * CONFIG.motion;
    const dy = randomBetween(window.innerWidth / -1000, window.innerWidth / 2000) * CONFIG.motion;
    const dHue = randomBetween(-CONFIG.hueDelta, CONFIG.hueDelta);

    let hue = randomBetween(
      targetColor.value.h - CONFIG.colorSpan / 2,
      targetColor.value.h + CONFIG.colorSpan / 2
    );
    if (hue < 0) hue += 360;
    if (hue >= 360) hue -= 360;
    const lightness = randomBetween(targetColor.value.l - 7.5, targetColor.value.l + 7.5);
    const saturation = randomBetween(targetColor.value.s - 15, targetColor.value.s + 15);

    const color = { ...targetColor.value, l: lightness, s: saturation };
    circles.value.push({ x, y, dx, dy, radius, dHue, color });
  }
};

const drawCircle = (circle: Circle) => {
  if (!ctx.value) return;
  ctx.value.beginPath();
  ctx.value.arc(circle.x, circle.y, circle.radius, 0, Math.PI * 2, false);
  ctx.value.fillStyle = `hsla(${circle.color.h}, ${circle.color.s}%, ${circle.color.l}%, 0.8)`;
  ctx.value.fill();
  ctx.value.closePath();
};

const animate = () => {
  if (!ctx.value) return;

  const now = performance.now();
  if (now - lastFrameTime < frameInterval) {
    animationFrameId = requestAnimationFrame(animate);
    return;
  }
  lastFrameTime = now;

  const canvasWidth = canvas.value?.width || 1;
  const canvasHeight = canvas.value?.height || 1;
  ctx.value.clearRect(0, 0, canvasWidth, canvasHeight);

  const maxHue = targetColor.value.h + CONFIG.colorSpan / 2;
  const minHue = targetColor.value.h - CONFIG.colorSpan / 2;

  circles.value.forEach(circle => {
    if (circle.x + circle.radius > canvasWidth || circle.x - circle.radius < 0) {
      circle.dx = -circle.dx;
    }
    if (circle.y + circle.radius > canvasHeight || circle.y - circle.radius < 0) {
      circle.dy = -circle.dy;
    }

    circle.x += circle.dx;
    circle.y += circle.dy;

    if (circle.color.h >= maxHue || circle.color.h <= minHue) {
      circle.dHue = -circle.dHue;
    }
    circle.color.h += circle.dHue;

    drawCircle(circle);
  });

  animationFrameId = requestAnimationFrame(animate);
};

const resizeCanvas = () => {
  if (!canvas.value) return;
  canvas.value.width = window.innerWidth * 1.1;
  canvas.value.height = window.innerHeight * 1.1;
  ctx.value = canvas.value.getContext('2d');
  createCircles();
};
useEventListener(window, 'resize', resizeCanvas);

watch(() => props.color, createCircles);

onMounted(() => {
  resizeCanvas();
  animate();
});

onUnmounted(() => {
  if (animationFrameId !== null) {
    cancelAnimationFrame(animationFrameId);
    animationFrameId = null;
  }
});
</script>

<template>
  <div class="motion-background" aria-hidden="true">
    <canvas ref="canvas" class="motion-background-canvas" />
    <div class="overlay"></div>
  </div>
</template>

<style scoped>
.motion-background {
  overflow: hidden;
  background: v-bind(color);
}

.overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: hsl(0deg 0% 41% / 25%);
  backdrop-filter: blur(70px);
}

.motion-background-canvas {
  transform: translate(-50%, -50%);
  top: 50%;
  left: 50%;
  position: absolute;
  display: block;
}
</style>
